import React, { useEffect, useState } from 'react';
import MarkdownView from './MarkdownView';

interface EditableMarkdownProps {
  content: string;
  title?: string;
  // When provided, an "Bearbeiten" → "Speichern" flow is enabled and the new
  // content is persisted via this callback. When omitted, the component is a
  // read-only Markdown view with a copy button.
  onSave?: (content: string) => Promise<void> | void;
  className?: string;
}

/**
 * Reusable view/edit widget for AI-generated Markdown.
 *
 * View mode renders formatted Markdown (via MarkdownView) inside a card with a
 * copy button. When `onSave` is supplied, an edit mode swaps in a monospace
 * textarea so the user can manually correct the output and persist it.
 */
const EditableMarkdown: React.FC<EditableMarkdownProps> = ({ content, title, onSave, className }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  // Re-seed the draft whenever the source content changes (e.g. a fresh
  // generation) — but not while the user is actively editing.
  useEffect(() => {
    if (!editing) setDraft(content);
  }, [content, editing]);

  const copy = () => {
    navigator.clipboard?.writeText(editing ? draft : content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    setError('');
    try {
      await onSave(draft);
      setEditing(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`card${className ? ` ${className}` : ''}`} style={{ marginTop: '12px' }}>
      <div className="card-header">
        {title ? <div className="card-title">{title}</div> : <div />}
        <div className="btn-group">
          {onSave && !editing && (
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
              Bearbeiten
            </button>
          )}
          {editing && (
            <>
              <button className="btn btn-success btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Speichern…' : 'Speichern'}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setDraft(content);
                  setEditing(false);
                  setError('');
                }}
                disabled={saving}
              >
                Abbrechen
              </button>
            </>
          )}
          <button className="btn btn-secondary btn-sm" onClick={copy}>
            {copied ? 'Kopiert ✓' : 'Kopieren'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginTop: '8px' }}>{error}</div>}

      {editing ? (
        <textarea
          className="form-textarea"
          style={{ fontFamily: 'monospace', fontSize: '13px', marginTop: '12px', width: '100%' }}
          rows={18}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      ) : (
        <MarkdownView content={content} style={{ marginTop: '12px' }} />
      )}
    </div>
  );
};

export default EditableMarkdown;
